export type UserRole = 'ADMIN' | 'GERENTE' | 'OPERADOR';

// Gestão de Perfis de Acesso — espelha a grade do backend
// (backend/src/access-profiles/perfis-padrao.ts). Gestão de usuários e de
// perfis não está na grade: só o dono da empresa faz isso.
export type PermissionModule =
    | 'LANCAMENTOS'
    | 'FLUXO_CAIXA'
    | 'PLANO_CONTAS'
    | 'RELATORIOS'
    | 'DASHBOARD'
    | 'IMPORTACOES'
    | 'FILIAIS_EMPRESAS';

export type PermissionAction = 'VIEW' | 'CREATE' | 'EDIT' | 'DELETE' | 'EXPORT';

export const PERMISSION_MODULE_ACTIONS: Record<PermissionModule, PermissionAction[]> = {
    LANCAMENTOS: ['VIEW', 'CREATE', 'EDIT', 'DELETE'],
    FLUXO_CAIXA: ['VIEW', 'CREATE', 'EDIT', 'DELETE'],
    PLANO_CONTAS: ['VIEW', 'CREATE', 'EDIT', 'DELETE'],
    RELATORIOS: ['VIEW', 'EXPORT'],
    DASHBOARD: ['VIEW'],
    IMPORTACOES: ['VIEW', 'CREATE', 'EDIT', 'DELETE'],
    FILIAIS_EMPRESAS: ['VIEW', 'CREATE', 'EDIT', 'DELETE'],
};

export const PERMISSION_MODULE_LABELS: Record<PermissionModule, string> = {
    LANCAMENTOS: 'Lançamentos',
    FLUXO_CAIXA: 'Fluxo de Caixa',
    PLANO_CONTAS: 'Plano de Contas',
    RELATORIOS: 'Relatórios',
    DASHBOARD: 'Dashboard',
    IMPORTACOES: 'Importações',
    FILIAIS_EMPRESAS: 'Filiais / Empresas',
};

export const PERMISSION_ACTION_LABELS: Record<PermissionAction, string> = {
    VIEW: 'Visualizar',
    CREATE: 'Criar',
    EDIT: 'Editar',
    DELETE: 'Excluir',
    EXPORT: 'Exportar',
};

export type EffectivePermissionsMap = Partial<Record<PermissionModule, Partial<Record<PermissionAction, boolean>>>>;

export interface PermissionItem {
    module: PermissionModule;
    action: PermissionAction;
    allowed: boolean;
}

export interface AccessProfile {
    id: string;
    name: string;
    description?: string | null;
    isSystem: boolean;
    /** Perfil global (padrão ou da equipe LTQ), que toda empresa pode atribuir. */
    global?: boolean;
    /** Quem está olhando pode editar este perfil. */
    editavel?: boolean;
    userCount: number;
    permissions: PermissionItem[];
    createdAt: string;
    updatedAt: string;
}

/** Empresa de que o usuario participa (GET /empresas/minhas). */
export interface EmpresaDoUsuario {
    id: string;
    name: string;
    /** O usuario e o dono: so ele ve Usuarios e Perfis de acesso. */
    souDono: boolean;
    /** Cobranca do dono: suspensa ou inativa bloqueia todos que participam. */
    estado: 'liberada' | 'suspensa' | 'inativa';
    /** Plano de quem paga a empresa: vale para todos que operam nela. */
    plano: {
        id: string;
        name: string;
        features: PlanFeature[];
        maxAccounts: number;
        maxTransactionsPerMonth: number;
        maxUsuarios: number;
    } | null;
    filiais: { id: string; name: string; isActive: boolean }[];
}

/** Convite pendente, na tela de Usuarios do dono. */
export interface ConviteDaEmpresa {
    id: string;
    email: string;
    accessProfileId: string | null;
    accessProfile?: { id: string; name: string } | null;
    filialIds: string[];
    expiraEm: string;
    expirado: boolean;
    status: 'PENDENTE' | 'ACEITO' | 'REVOGADO';
    createdAt: string;
    /** So na criacao e no reenvio: o e-mail saiu? Se nao, a tela avisa. */
    emailEnviado?: boolean;
}

/** Dados do convite para a tela de aceite. */
export interface ConvitePublico {
    email: string;
    empresa: string;
    convidadoPor: string | null;
    expiraEm: string;
    expirado: boolean;
    aceito: boolean;
    contaExiste: boolean;
}

/** Participante de uma empresa, na tela de Usuarios do dono. */
export interface MembroDaEmpresa {
    userId: string;
    name: string;
    email: string;
    souDono: boolean;
    accessProfile: { id: string; name: string } | null;
    overrides: PermissionItem[];
    filiais: { id: string; name: string }[];
    desde: string;
}

export type PlanFeature =
    | 'dashboard'
    | 'graficos'
    | 'accounts'
    | 'transactions'
    | 'cashflow'
    | 'reports'
    | 'budget'
    | 'recurring'
    | 'goals'
    | 'investments'
    | 'settings'
    | 'export_pdf'
    | 'export_excel'
    | 'multiple_accounts'
    | 'custom_categories'
    | 'api_access'
    | 'priority_support'
    | 'import'
    // Usuarios e Perfis de acesso em Configuracoes (convidar pessoas, perfis e
    // participantes). Configuracoes em si abre em qualquer plano.
    | 'usuarios'
    // Multiplos negocios. Libera o seletor de negocio e a tela de troca; sem
    // ela o usuario opera so no contexto que ja tem, sem escolher.
    | 'filiais';

export interface Plan {
    id: string;
    name: string;
    description: string;
    price: number;
    interval: 'monthly' | 'yearly';
    features: PlanFeature[];
    isActive: boolean;
    maxAccounts: number;
    maxTransactionsPerMonth: number;
    /** Pessoas que o dono pode ter na empresa além dele (0 = não convida). */
    maxUsuarios?: number;
    createdAt: string;
    updatedAt: string;
}

export interface User {
    id: string;
    name: string;
    email: string;
    avatar?: string;
    role: UserRole;
    planId?: string;
    planExpiresAt?: string;
    status: 'active' | 'inactive' | 'suspended';
    createdAt?: string;
    onboardingCompletedAt?: string | null;
    surveyOnboardingRequired?: boolean;
    surveyOnboardingCompletedAt?: string | null;
}



export interface CashFlowData {
    month: string;
    income: number;
    expense: number;
}

export interface DashboardSummary {
    totalIncome: number;
    totalExpenses: number;
    resultado: number;
}



export interface Category {
    id: string;
    name: string;
    type: 'income' | 'expense';
    icon: string;
    color: string;
}

export interface Report {
    id: string;
    name: string;
    type: 'monthly' | 'yearly' | 'custom';
    startDate: string;
    endDate: string;
    createdAt: string;
}

export interface ExpenseByCategory {
    category: string;
    amount: number;
    percentage: number;
    color: string;
    icon: string;
}

export interface IncomeBySource {
    source: string;
    amount: number;
    percentage: number;
    color: string;
}



// Fluxo de Caixa - Movimentação
export interface CashFlowTransaction {
    id: string;
    description: string;
    amount: number;
    type: 'INCOME' | 'EXPENSE';
    category: string;
    date: string;
    notes?: string;
}

// Fluxo de Caixa - Resumo
export interface CashFlowSummary {
    totalIncome: number;
    totalExpense: number;
    balance: number;
}

// FAQ
export interface FAQ {
    id: string;
    question: string;
    answer: string;
    category: string;
    order: number;
}

// Forma de Pagamento
export interface PaymentMethod {
    id: string;
    name: string;
    description?: string;
    isActive: boolean;
}

// Plano de Contas
export type AccountType = 'REVENUE' | 'COST' | 'EXPENSE';

export interface ChartOfAccount {
    id: string;
    code: string;
    name: string;
    type: AccountType;
    parentId?: string;
    parent?: ChartOfAccount;
    children?: ChartOfAccount[];
    level: number;
    isActive: boolean;
}



export interface DistribuicaoCategoria {
    categoria: string;
    valor: number;
    percentual: number;
}

export interface DateFilter {
    dataInicio: string;
    dataFim: string;
}

